<script setup lang="ts">
import { computed, ref } from 'vue';
import { Guest } from '@/types/guest';
import AnswerGuestsPopup from '@/components/GuestList/AnswerGuestsPopup/AnswerGuestsPopup.vue';

const props = defineProps<{
  guests: Guest[];
}>();

const showGuestsPopup = ref(false);
const selectedQuestionId = ref('');
const selectedAnswer = ref('');

const formatAnswer = (answer: string | number | boolean | (string | number | boolean)[] | undefined): string => {
  if (answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0))
    return 'Brak odpowiedzi';
  if (typeof answer === 'boolean') return answer ? 'Tak' : 'Nie';
  if (Array.isArray(answer)) return answer.join(', ');
  return String(answer);
};

const isAnswerEmpty = (answer: string | number | boolean | (string | number | boolean)[] | undefined): boolean => {
  return answer === '' || answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0);
};

const answerStats = computed(() => {
  const stats = new Map<string, { answers: Record<string, number>; total: number }>();

  props.guests.forEach((guest) => {
    guest.additionalQuestions.forEach((question) => {
      if (isAnswerEmpty(question.answer)) {
        return;
      }

      if (!stats.has(question.id)) {
        stats.set(question.id, { answers: {}, total: 0 });
      }

      const questionStats = stats.get(question.id)!;
      const answer = formatAnswer(question.answer);

      questionStats.answers[answer] = (questionStats.answers[answer] || 0) + 1;
      questionStats.total += 1;
    });
  });

  return stats;
});

const getGuestsForAnswer = (questionId: string, answer: string): Guest[] => {
  return props.guests.filter((guest) => {
    const question = guest.additionalQuestions.find((q) => q.id === questionId);
    if (!question || isAnswerEmpty(question.answer)) {
      return false;
    }
    return formatAnswer(question.answer) === answer;
  });
};

const selectedGuests = computed(() => {
  if (!selectedQuestionId.value || !selectedAnswer.value) {
    return [];
  }
  return getGuestsForAnswer(selectedQuestionId.value, selectedAnswer.value);
});

const onAnswerClick = (questionId: string, answer: string): void => {
  selectedQuestionId.value = questionId;
  selectedAnswer.value = answer;
  showGuestsPopup.value = true;
};

const onCloseGuestsPopup = (): void => {
  showGuestsPopup.value = false;
};
</script>

<template>
  <div class="answer-stats">
    <template
      v-for="[questionId, { answers, total }] in answerStats"
      :key="questionId"
    >
      <div class="answer-stats__card">
        <div class="answer-stats__content">
          <div class="answer-stats__info">
            <div class="answer-stats__title">
              <div class="answer-stats__title-main">{{ questionId }}</div>
            </div>
            <div class="answer-stats__answers">
              <button
                v-for="(count, answer) in answers"
                :key="answer"
                type="button"
                class="answer-stats__answer"
                @click="onAnswerClick(questionId, answer)"
              >
                <div class="answer-stats__answer-text">{{ answer }}</div>
                <div class="answer-stats__values">
                  <div class="answer-stats__value">{{ count }}</div>
                  <div class="answer-stats__percentage">({{ Math.round((count / total) * 100) }}%)</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <AnswerGuestsPopup
      :is-visible="showGuestsPopup"
      :question-id="selectedQuestionId"
      :answer="selectedAnswer"
      :guests="selectedGuests"
      @close="onCloseGuestsPopup"
    />
  </div>
</template>

<style lang="scss" scoped>
.answer-stats {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;

  &__card {
    width: 100%;
    border-radius: 12px;
    background: #fff;
    position: relative;
    transition: all 0.3s ease;
    overflow: hidden;
    border: 1px solid rgba($black-100, 0.1);
    box-shadow: 0 1px 3px rgba($black-100, 0.1);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba($black-100, 0.1);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
  }

  &__title {
    color: $black-300;
    padding: 2rem;
    background: linear-gradient(to right, rgba($brown-200, 0.05), rgba($brown-200, 0.1));
    border-bottom: 1px solid rgba($brown-200, 0.15);
    margin-bottom: 1rem;

    &-main {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.3rem;
    }
  }

  &__answers {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 2rem 2rem;
  }

  &__answer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid rgba($black-100, 0.1);
    background: transparent;
    cursor: pointer;
    text-align: left;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: rgba($brown-200, 0.08);
      transform: translateX(4px);
    }

    &:focus {
      outline: 2px solid $brown-200;
      outline-offset: 2px;
    }
  }

  &__answer-text {
    color: $black-300;
    font-size: 1.4rem;
  }

  &__values {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__value {
    color: $brown-200;
    font-size: 1.8rem;
    font-weight: bold;
  }

  &__percentage {
    color: $black-300;
    font-size: 1.2rem;
  }

  &__icon-wrapper {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $white;
    color: $brown-200;
  }

  &__icon {
    font-size: 1.5rem;
  }
}
</style>
